import React, { useMemo } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Paper,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { tokens } from '../../../theme';
import {
    useDisableReleaseProductMutation,
    useGetReleaseProductDeliveriesQuery,
} from '../../../features/organizations/organizationsApiSlice';

const normalizeFormats = (formats) => {
    if (!Array.isArray(formats)) return [];

    return [...new Set(
        formats
            .map((fmt) => String(fmt || '').trim().toLowerCase())
            .filter(Boolean)
    )].sort();
};

const normalizeS3Uri = (value) => {
    if (!value) return '';

    const cleaned = String(value)
        .trim()
        .replace(/^s3:\/\//i, '')
        .replace(/^\/+|\/+$/g, '');

    return cleaned ? `s3://${cleaned}` : '';
};

const buildProductFolder = (name) => {
    return String(name || 'product')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
};

const getAssetId = (asset) => {
    return asset?._id || asset?.id || asset?.dataAssetId || asset?.data_asset_id || '';
};

const getOrgDeliverySummary = (organization) => {
    const s3Config = organization?.s3_config || organization?.s3Config || {};
    const onpremConfig = s3Config?.infra_data?.onprem_config;
    const snowflakeConfig = s3Config?.infra_data?.snowflake_config;

    const bucketCreated = !!onpremConfig?.bucket_created;
    const onpremBucket = onpremConfig?.onprem_bucket;
    const s3Uri = onpremConfig?.s3_uri;

    const s3Base = onpremBucket
        ? `s3://${String(onpremBucket).replace(/^s3:\/\//i, '').replace(/\/+$/g, '')}`
        : normalizeS3Uri(s3Uri);

    const s3Ready = Boolean(onpremConfig && (bucketCreated || onpremBucket || s3Uri));

    const snowflakeReady = Boolean(
        snowflakeConfig?.database &&
        snowflakeConfig?.schema
    );

    const preferredFormats = normalizeFormats(
        organization?.delivery_format ||
        organization?.deliveryFormat ||
        organization?.delivery_formats ||
        []
    );

    return {
        s3Ready,
        s3Base,
        snowflakeReady,
        snowflakeConfig,
        preferredFormats,
    };
};

const getFormatLabel = (fmt) => {
    if (fmt === 'csv') return 'S3 CSV';
    if (fmt === 'parquet') return 'S3 Parquet';
    if (fmt === 'snowflake') return 'Snowflake Copy';
    return fmt;
};

const formatDate = (value) => {
    if (!value) return '-';

    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return String(value);

    return dt.toLocaleString();
};

const ManageReleaseProduct = ({
    open,
    onClose,
    onDisabled,
    asset,
    productName,
    organization,
    organizationId,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const resolvedProductName = asset?.name || productName || '';
    const resolvedAssetId = getAssetId(asset);

    const delivery = useMemo(
        () => getOrgDeliverySummary(organization),
        [organization]
    );

    const productFolder = useMemo(
        () => buildProductFolder(resolvedProductName),
        [resolvedProductName]
    );

    const effectiveFormats = useMemo(() => {
        const formats = delivery.preferredFormats;

        if (formats.length) return formats;

        const inferred = [];

        if (delivery.s3Ready) {
            inferred.push('csv');
            inferred.push('parquet');
        }

        if (delivery.snowflakeReady) {
            inferred.push('snowflake');
        }

        return inferred;
    }, [delivery]);

    const id = organizationId || organization?.organization_id || organization?.id;

    const {
        data: deliveriesData,
        isLoading: loadingDeliveries,
        isFetching: fetchingDeliveries,
        isError: deliveriesError,
    } = useGetReleaseProductDeliveriesQuery(
        {
            id,
            product: resolvedProductName,
            data_asset_id: resolvedAssetId,
        },
        {
            skip: !open || !id || !resolvedProductName,
        }
    );

    const [disableReleaseProduct, {
        isLoading: disabling,
        error: disableError,
    }] = useDisableReleaseProductMutation();

    const deliveryRows = Array.isArray(deliveriesData?.rows) ? deliveriesData.rows : [];
    const loadingRows = loadingDeliveries || fetchingDeliveries;

    const renderDestination = (fmt) => {
        if (fmt === 'csv' || fmt === 'parquet') {
            if (!delivery.s3Ready) {
                return 'S3 is not configured for this organization.';
            }

            return `${delivery.s3Base}/outgoing/${productFolder}/`;
        }

        if (fmt === 'snowflake') {
            if (!delivery.snowflakeReady) {
                return 'Snowflake is not configured for this organization.';
            }

            const db = delivery.snowflakeConfig?.database;
            const schema = delivery.snowflakeConfig?.schema;
            const table = delivery.snowflakeConfig?.table;

            return table ? `${db}.${schema}.${table}` : `${db}.${schema}`;
        }

        return 'Unknown delivery target.';
    };

    const handleDisable = async () => {
        if (!id || !resolvedProductName || disabling) return;

        await disableReleaseProduct({
            id,
            product: resolvedProductName,
            data_asset_id: resolvedAssetId || undefined,
        }).unwrap();

        onDisabled?.(resolvedProductName);
    };

    return (
        <Dialog
            open={open}
            onClose={disabling ? undefined : onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    background: `linear-gradient(135deg, ${colors.primary[400]}, ${colors.primary[900]})`,
                    borderRadius: '18px',
                    border: `1px solid ${colors.primary[300]}`,
                },
            }}
        >
            <DialogTitle>
                Manage Release Product
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="h4" color="textPrimary">
                            {resolvedProductName || 'Release Product'}
                        </Typography>

                        <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                            {asset?.release ? (
                                <Chip
                                    label={asset.release}
                                    sx={{
                                        bgcolor: colors.blueAccent[700],
                                        color: colors.grey[100],
                                    }}
                                />
                            ) : null}

                            <Chip
                                label="Enabled"
                                sx={{
                                    bgcolor: colors.greenAccent[700],
                                    color: colors.grey[100],
                                }}
                            />
                        </Box>
                    </Box>

                    {disableError ? (
                        <Alert severity="error" variant="filled">
                            {disableError?.data?.message || disableError?.error || 'Failed to disable release product.'}
                        </Alert>
                    ) : null}

                    <Divider sx={{ borderColor: colors.primary[300] }} />

                    <Box>
                        <Typography variant="h5" color="textPrimary" mb={1}>
                            Delivery Configuration
                        </Typography>

                        <Typography variant="body2" color="textSecondary">
                            This product is enabled for this organization. Future release deliveries
                            will use the organization’s configured delivery preferences below.
                        </Typography>
                    </Box>

                    {!effectiveFormats.length ? (
                        <Alert severity="warning" variant="filled">
                            This organization does not currently have preferred delivery formats
                            or delivery targets configured.
                        </Alert>
                    ) : (
                        <Stack spacing={1.5}>
                            {effectiveFormats.map((fmt) => {
                                const isS3 = fmt === 'csv' || fmt === 'parquet';
                                const isSnowflake = fmt === 'snowflake';
                                const routeReady = isS3
                                    ? delivery.s3Ready
                                    : isSnowflake
                                        ? delivery.snowflakeReady
                                        : false;

                                return (
                                    <Paper
                                        key={fmt}
                                        elevation={1}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: '12px',
                                            background: colors.primary[500],
                                            border: `1px solid ${routeReady
                                                    ? colors.greenAccent[600]
                                                    : colors.redAccent[600]
                                                }`,
                                        }}
                                    >
                                        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                                            <Chip
                                                size="small"
                                                label={getFormatLabel(fmt)}
                                                sx={{
                                                    bgcolor: routeReady
                                                        ? colors.greenAccent[700]
                                                        : colors.redAccent[700],
                                                    color: colors.grey[100],
                                                }}
                                            />

                                            <Typography variant="body2" color="textSecondary">
                                                {routeReady ? 'Ready' : 'Not configured'}
                                            </Typography>
                                        </Box>

                                        <Typography
                                            variant="body2"
                                            color="textPrimary"
                                            mt={1}
                                            sx={{ wordBreak: 'break-all' }}
                                        >
                                            {renderDestination(fmt)}
                                        </Typography>
                                    </Paper>
                                );
                            })}
                        </Stack>
                    )}

                    <Divider sx={{ borderColor: colors.primary[300] }} />

                    <Box>
                        <Typography variant="h5" color="textPrimary" mb={1}>
                            Current Deliveries
                        </Typography>

                        <Typography variant="body2" color="textSecondary" mb={2}>
                            Existing current_delivery rows for this product on this organization.
                        </Typography>

                        {loadingRows ? (
                            <Box display="flex" alignItems="center" gap={1}>
                                <CircularProgress size={22} color="inherit" />
                                <Typography variant="body2" color="textSecondary">
                                    Loading current deliveries...
                                </Typography>
                            </Box>
                        ) : deliveriesError ? (
                            <Alert severity="error" variant="filled">
                                Failed to load current deliveries for this product.
                            </Alert>
                        ) : deliveryRows.length ? (
                            <Stack spacing={1.25}>
                                {deliveryRows.map((row, idx) => (
                                    <Paper
                                        key={`${row.delivery_run_id || 'run'}-${row.product}-${row.version}-${idx}`}
                                        elevation={1}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: '12px',
                                            background: colors.primary[500],
                                            border: `1px solid ${colors.primary[300]}`,
                                        }}
                                    >
                                        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                                            <Chip
                                                size="small"
                                                label={row.release}
                                                sx={{
                                                    bgcolor: colors.blueAccent[700],
                                                    color: colors.grey[100],
                                                }}
                                            />

                                            <Chip
                                                size="small"
                                                label={row.status}
                                                sx={{
                                                    bgcolor:
                                                        row.status === 'failed'
                                                            ? colors.redAccent[700]
                                                            : row.status === 'delivered'
                                                                ? colors.greenAccent[700]
                                                                : colors.primary[300],
                                                    color: colors.grey[100],
                                                }}
                                            />

                                            <Chip
                                                size="small"
                                                label={row.delivery_target_type}
                                                sx={{
                                                    bgcolor: colors.primary[300],
                                                    color: colors.grey[100],
                                                }}
                                            />

                                            {row.file_format ? (
                                                <Chip
                                                    size="small"
                                                    label={row.file_format}
                                                    sx={{
                                                        bgcolor: colors.primary[300],
                                                        color: colors.grey[100],
                                                    }}
                                                />
                                            ) : null}

                                            <Chip
                                                size="small"
                                                label={`Version ${row.version}`}
                                                sx={{
                                                    bgcolor: colors.primary[300],
                                                    color: colors.grey[100],
                                                }}
                                            />
                                        </Box>

                                        <Typography
                                            variant="body2"
                                            color="textPrimary"
                                            mt={1}
                                            sx={{ wordBreak: 'break-all' }}
                                        >
                                            {row.delivery_target_type === 's3'
                                                ? row.full_s3_uri || 'No S3 destination stored'
                                                : `${row.snowflake_db || ''}.${row.snowflake_schema || ''}.${row.snowflake_table || ''}`}
                                        </Typography>

                                        <Typography variant="caption" color="textSecondary" display="block" mt={0.5}>
                                            Delivery date: {formatDate(row.delivery_date_utc)}
                                        </Typography>

                                        {row.delivery_run_id ? (
                                            <Typography variant="caption" color="textSecondary" display="block">
                                                Run ID: {row.delivery_run_id}
                                            </Typography>
                                        ) : null}

                                        {row.last_error ? (
                                            <Alert severity="error" sx={{ mt: 1 }}>
                                                {row.last_error}
                                            </Alert>
                                        ) : null}
                                    </Paper>
                                ))}
                            </Stack>
                        ) : (
                            <Alert severity="info" variant="outlined">
                                No current_delivery rows found for this product.
                            </Alert>
                        )}
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button
                    variant="contained"
                    onClick={onClose}
                    disabled={disabling}
                    sx={{
                        backgroundColor: '#ffffff',
                        color: colors.grey[900],
                        fontWeight: 700,
                        '&:hover': {
                            backgroundColor: colors.grey[200],
                        },
                    }}
                >
                    Close
                </Button>

                <Button
                    variant="contained"
                    onClick={handleDisable}
                    disabled={!resolvedProductName || disabling}
                    sx={{
                        backgroundColor: colors.redAccent[600],
                        color: colors.grey[100],
                        fontWeight: 700,
                        border: `1px solid ${colors.redAccent[400]}`,
                        '&:hover': {
                            backgroundColor: colors.redAccent[500],
                        },
                    }}
                >
                    {disabling ? 'Disabling...' : 'Disable Product'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ManageReleaseProduct;